import $ from "jquery";

import render_sign_message_receipt_modal from "../templates/sign_message_receipt_modal.hbs";

import * as channel from "./channel.ts";
import * as dialog_widget from "./dialog_widget.ts";
import {$t, $t_html} from "./i18n.ts";
import {
    type MessageContainer,
    type MessageGroup,
    get_timestr,
    populate_group_from_message,
} from "./message_list_view.ts";
import type {Message} from "./message_store.ts";
import * as people from "./people.ts";
import {update_elements} from "./rendered_markdown.ts";
import * as rows from "./rows.ts";
import * as stream_data from "./stream_data.ts";
import {process_submessages} from "./submessage.ts";
import * as ui_report from "./ui_report.ts";
import {toggle_user_card_popover_for_message} from "./user_card_popover.ts";
import {current_user} from "./state_data.ts";

function register_message_preview_click_handlers(
    $message_preview_container: JQuery,
    sender_id: number,
): void {
    $message_preview_container.on("mouseover", ".sender_info_hover", function (this: HTMLElement) {
        const $row = $(this).closest(".message_row");
        $row.addClass("sender_info_hovered");
    });

    $message_preview_container.on("mouseout", ".sender_info_hover", function (this: HTMLElement) {
        const $row = $(this).closest(".message_row");
        $row.removeClass("sender_info_hovered");
    });

    $message_preview_container.on(
        "click",
        ".sender_name, .inline-profile-picture-wrapper",
        function (this: HTMLElement, e) {
            e.stopPropagation();
            toggle_user_card_popover_for_message(this, sender_id, sender_id, true);
        },
    );
}

function get_message_group_for_message_preview(message: Message): MessageGroup {
    const message_group = populate_group_from_message(message, false, false, undefined);
    if (message_group.is_stream) {
        message_group.user_can_resolve_topic = false;
        message_group.is_topic_editable = false;
    }
    return message_group;
}

function get_message_container_for_preview(message: Message): MessageContainer {
    const computed_variables = {
        include_sender: true,
        is_hidden: false,
        msg: message,
        sender_is_bot: people.sender_is_bot(message),
        sender_is_deactivated: people.sender_is_deactivated(message),
        sender_is_guest: people.sender_is_guest(message),
        should_add_guest_indicator_for_sender: people.should_add_guest_user_indicator(
            message.sender_id,
        ),
        small_avatar_url: people.small_avatar_url(message),
        status_message: "",
        timestr: get_timestr(message),
        want_date_divider: false,
        want_subscription_status_divider: false,
    };
    const unused_variables = {
        date_divider_html: undefined,
        edited: false,
        include_recipient: false,
        last_edit_timestamp: undefined,
        last_moved_timestamp: undefined,
        mention_classname: undefined,
        message_edit_notices_alongside_sender: false,
        message_edit_notices_for_status_message: false,
        message_edit_notices_in_left_col: false,
        modified: false,
        moved: false,
        widget_edited: false,
        year_changed: false,
    };
    return {
        ...computed_variables,
        ...unused_variables,
    };
}

function post_process_message_preview($row: JQuery): void {
    const $content = $row.find(".message_content");
    update_elements($content);
    const id = rows.id($row);
    process_submessages({
        $row,
        message_id: id,
    });
    const $widget_content = $content.find(".widget-content");
    $widget_content.find("button, input, select").prop("disabled", true);
    $widget_content.find(".poll-edit-question, .poll-option-bar").hide();
}

export function show_sign_message_receipt_modal(message: Message): void {
    let invite_only;
    let is_web_public;
    let is_archived;
    if (message.is_stream) {
        const stream_id = message.stream_id;
        invite_only = stream_data.is_invite_only_by_stream_id(stream_id);
        is_web_public = stream_data.is_web_public(stream_id);
        is_archived = stream_data.is_stream_archived_by_id(stream_id);
    }

    const message_preview_body_args = get_message_container_for_preview(message);
    const modal_content_html = render_sign_message_receipt_modal({
        recipient_row_data: get_message_group_for_message_preview(message),
        message_container_data: {
            ...message_preview_body_args,
            message_list_id: "",
        },
        invite_only,
        is_web_public,
        is_archived,
    });

    function submit_message_receipt_signature(action: "sign" | "void"): void {
        const url = "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature";
        void channel.post({
            url,
            data: {action},
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function submit_message_receipt_visibility(visibility: "private" | "public"): void {
        const url =
            "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature/visibility";
        void channel.post({
            url,
            data: {visibility},
            cache: false,
            success() {
                dialog_widget.close();
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                dialog_widget.hide_dialog_spinner();
            },
        });
    }

    function add_visibility_button(
        $modal: JQuery,
        can_toggle_signature_page_visibility: boolean,
        signature_page_is_public: boolean,
    ): void {
        if (!can_toggle_signature_page_visibility || current_user.user_id !== message.sender_id) {
            return;
        }

        const visibility = signature_page_is_public ? "private" : "public";
        const button_label = signature_page_is_public
            ? $t({defaultMessage: "Make private"})
            : $t({defaultMessage: "Make public"});
        const $visibility_button = $(
            `<button type="button" class="modal__button sign-message-receipt-visibility-button">
                ${button_label}
            </button>`,
        );

        $modal.find(".modal__footer .dialog_exit_button").before($visibility_button);
        $visibility_button.on("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            dialog_widget.show_dialog_spinner();
            submit_message_receipt_visibility(visibility);
        });
    }

    function post_render(): void {
        const $message_preview_container = $("#sign-message-receipt-preview-container");
        register_message_preview_click_handlers($message_preview_container, message.sender_id);
        post_process_message_preview($message_preview_container.find(".message_row"));

        const $modal = $("#sign_message_receipt_modal");
        const $submit_button = $modal.find(".dialog_submit_button");
        const $submit_button_text = $submit_button.find(".submit-button-text");
        const $submit_button_container = $modal.find(".modal__footer .dialog_submit_button_container");
        $modal.find(".dialog_submit_button").addClass("sign-message-receipt-sign-button");

        const visibility_url =
            "/json/messages/" + encodeURIComponent(message.id) + "/receipt_signature/visibility";
        void channel.get({
            url: visibility_url,
            cache: false,
            success(data) {
                add_visibility_button(
                    $modal,
                    data.can_toggle_signature_page_visibility,
                    data.signature_page_is_public,
                );

                if (!data.show_signing_buttons) {
                    $submit_button_container.hide();
                    return;
                }

                const void_button_label = data.show_sign_edited_post_button
                    ? $t({defaultMessage: "VOID EDITED POST"})
                    : $t({defaultMessage: "VOID"});
                const submit_button_label = data.show_sign_edited_post_button
                    ? $t({defaultMessage: "SIGN EDITED POST"})
                    : $t({defaultMessage: "SIGN"});

                const $void_button = $(
                    `<button type="button" class="modal__button sign-message-receipt-void-button">${void_button_label}</button>`,
                );
                $submit_button_text.text(submit_button_label);
                $submit_button_container.before($void_button);
                $void_button.on("click", (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dialog_widget.show_dialog_spinner();
                    submit_message_receipt_signature("void");
                });
            },
            error(xhr) {
                ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
            },
        });
    }

    dialog_widget.launch({
        html_heading: $t_html({
            defaultMessage: "Sign message receipt",
        }),
        html_body: modal_content_html,
        html_submit_button: $t_html({defaultMessage: "SIGN"}),
        html_exit_button: $t_html({defaultMessage: "Cancel"}),
        id: "sign_message_receipt_modal",
        form_id: "sign_message_receipt_form",
        on_click: () => {
            submit_message_receipt_signature("sign");
        },
        post_render,
        loading_spinner: true,
    });
}
